/**
 * Spanish labels for subscription statuses.
 *
 * The DB / API store the canonical English status string (`active`, `trial`,
 * …); this maps them to Spanish for display. The platform-admin is Spanish-only
 * (no i18n) and the shared `<Badge>` must stay language-neutral, so the label is
 * applied at the call site via `subStatusLabel()`.
 */
export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  // Vocabulario del motor de acceso (services/access.py): sanos, mora, terminales.
  active: 'Activa',
  trial: 'En prueba',
  trialing: 'En prueba',
  paused: 'Pausada',
  past_due: 'Pago vencido',
  cancelled: 'Cancelada',
  expired: 'Expirada',
  incomplete: 'Checkout sin completar',
};

/** Returns the Spanish label for a status, falling back to the raw value. */
export function subStatusLabel(status: string): string {
  return SUBSCRIPTION_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/**
 * The ONE reading of a subscription row, derived by the backend
 * (`services/subscription_display.py`) and sent as `display_state` on every
 * payload that carries a subscription. The SPA never derives it again: the
 * old `isScheduledCancel` ignored the date, so a cancellation whose period
 * ended weeks ago still read "fin de ciclo".
 *
 *   none             — nobody bills it (Hobby, comp plans, hand-made rows)
 *   active           — a provider (Polar) is billing it
 *   scheduled_cancel — cancellation requested, period still running
 *   stale_cancel     — period over but the row still says active: behind the
 *                      provider, the 3-hourly pass reconciles it
 *   cancelled        — terminal; stays in the history as "paid and left"
 *   past_due         — the provider is retrying a failed charge
 *   paused           — the provider stopped charging on purpose (a Polar
 *                      pause); the workshop is on the free plan meanwhile
 */
/** Nombre del proveedor de una suscripción para pintarlo. Polar es el único
 * canal de cobro; una fila de un canal retirado enseña su nombre tal cual. */
export function providerLabel(provider?: string | null): string {
  const p = (provider || 'polar').toLowerCase();
  return p === 'polar' ? 'Polar' : p;
}

export type SubscriptionDisplayState =
  | 'none' | 'active' | 'scheduled_cancel' | 'stale_cancel' | 'cancelled' | 'past_due' | 'paused';

export const DISPLAY_STATE_OPTIONS: Array<{ value: SubscriptionDisplayState; label: string }> = [
  { value: 'none', label: 'Sin suscripción' },
  { value: 'active', label: 'Activa' },
  { value: 'scheduled_cancel', label: 'Cancela a fin de ciclo' },
  { value: 'stale_cancel', label: 'Cancelada · pendiente de confirmar' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'past_due', label: 'Impago' },
  { value: 'paused', label: 'Pausada' },
];

/** Badge tone (a `StatusBadge` status key) per display state. */
const DISPLAY_STATE_TONE: Record<SubscriptionDisplayState, string> = {
  none: 'inactive',
  active: 'active',
  scheduled_cancel: 'past_due',
  stale_cancel: 'past_due',
  cancelled: 'cancelled',
  past_due: 'past_due',
  paused: 'past_due',
};

type DisplayableSub = {
  display_state?: SubscriptionDisplayState | string | null;
  period_end?: string | null;
  cancelled_at?: string | null;
};

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('es-ES') : null);

/**
 * Badge props for a subscription: `{ status: <tone key>, label }`. The label
 * carries the date where it matters — "Cancela el 25/8/2026" while the period
 * runs, "Cancelada el 25/8/2026" once terminal (the period end, which is when
 * the provider revoked it; the request date otherwise).
 */
export function displayStateBadge(sub: DisplayableSub | null | undefined): { status: string; label: string } {
  const state = (sub?.display_state as SubscriptionDisplayState | undefined) ?? 'none';
  const base = DISPLAY_STATE_OPTIONS.find((o) => o.value === state)?.label ?? state;
  const tone = DISPLAY_STATE_TONE[state] ?? 'inactive';
  const end = fmtDate(sub?.period_end) ?? fmtDate(sub?.cancelled_at);
  if (state === 'scheduled_cancel' && end) return { status: tone, label: `Cancela el ${end}` };
  if (state === 'cancelled' && end) return { status: tone, label: `Cancelada el ${end}` };
  return { status: tone, label: base };
}

/** Spanish label for the billing cadence ('month' | 'year'). */
export function cadenceLabel(interval?: string | null): string {
  if (interval === 'year') return 'Anual';
  if (interval === 'month') return 'Mensual';
  return '—';
}

/**
 * Free-trial phase — orthogonal to status/plan/cadence. A subscription is "in
 * trial" while ``trial_ends_at`` is set and still in the future. The trial is
 * NOT a status value: a trialing subscription is `active` (it has access).
 */
export function isInTrial(sub: { trial_ends_at?: string | null }): boolean {
  if (!sub.trial_ends_at) return false;
  const ends = new Date(sub.trial_ends_at).getTime();
  return Number.isFinite(ends) && ends > Date.now();
}

/** "En prueba · hasta DD/MM/AAAA" while on trial, else null. */
export function trialBadgeLabel(trial_ends_at?: string | null): string | null {
  if (!isInTrial({ trial_ends_at })) return null;
  return `En prueba · hasta ${new Date(trial_ends_at as string).toLocaleDateString('es-ES')}`;
}
